import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ParentAdmissionPortal() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState('search'); // search, school, seats, exam, apply, done, compare
  const [schools, setSchools] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchClass, setSearchClass] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [seats, setSeats] = useState(null);
  const [exams, setExams] = useState([]);
  const [applyForm, setApplyForm] = useState({ student_name: '', parent_name: '', parent_phone: '', class_name: '', address: '' });
  const [applied, setApplied] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [nearbySchools, setNearbySchools] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [locatingUser, setLocatingUser] = useState(false);

  const classOptions = ['Nursery', 'LKG', 'UKG', ...Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`)];

  const findNearby = () => {
    setLocatingUser(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setUserLocation(loc);
          try {
            const res = await fetch(`${API_URL}/api/school-match/portal/nearby?lat=${loc.lat}&lng=${loc.lng}&radius_km=10&limit=20`);
            const data = await res.json();
            if (data.success) setNearbySchools(data.schools || []);
          } catch (err) { console.error(err); }
          setShowMap(true);
          setLocatingUser(false);
        },
        () => { setLocatingUser(false); }
      );
    }
  };


  const searchSchools = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (searchClass) params.append('class_name', searchClass);
      params.append('limit', '20');
      const res = await fetch(`${API_URL}/api/school-match/portal/schools?${params}`);
      const data = await res.json();
      if (data.success) setSchools(data.schools || []);
    } catch (err) {
      console.error('Search error:', err);
    }
    setLoading(false);
  };

  const selectSchool = async (school) => {
    setSelectedSchool(school);
    setStep('school');
    try {
      const sid = school.school_id || school.id;
      const [seatsRes, examsRes] = await Promise.all([
        fetch(`${API_URL}/api/admission/seat/live/${sid}`).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/admission/exam/list-public/${sid}`).then(r => r.json()).catch(() => ({ exams: [] })),
      ]);
      if (seatsRes?.success) setSeats(seatsRes);
      setExams(examsRes?.exams || []);
    } catch (err) { console.error(err); }
  };

  const submitApplication = async () => {
    setLoading(true);
    try {
      const sid = selectedSchool?.school_id || selectedSchool?.id;
      await fetch(`${API_URL}/api/admission/parent/express-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          school_id: sid,
          parent_name: applyForm.parent_name,
          phone: applyForm.parent_phone,
          student_name: applyForm.student_name,
          class_interested: applyForm.class_name,
          source: 'admission_portal',
        })
      });
      setApplied(true);
      setStep('done');
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // Search Screen
  if (step === 'search') return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <span className="text-xl font-black text-orange-600" style={{ fontFamily: 'Outfit, sans-serif' }}>शिक्षा मित्र</span>
            <span className="text-xs text-gray-400 hidden sm:inline">Admission Portal</span>
          </div>
          <a href="/login" className="px-4 py-1.5 text-sm text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50">
            {language === 'hi' ? 'School Login' : 'School Login'}
          </a>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 pt-12 pb-8 text-center" data-testid="portal-search">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-800 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {language === 'hi' ? 'अपने बच्चे के लिए सही स्कूल खोजें' : 'Find the Right School for Your Child'}
        </h1>
        <p className="text-gray-500 text-base sm:text-lg max-w-xl mx-auto mb-8">
          {language === 'hi' ? 'Search करें, Seat check करें, Exam दें, Apply करें — सब एक जगह' : 'Search, Check Seats, Take Exam, Apply — All in One Place'}
        </p>

        {/* Search Box */}
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg p-4 sm:p-6 border">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text" value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchSchools()}
              placeholder={language === 'hi' ? 'School का नाम या area लिखें...' : 'School name or area...'}
              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-base"
              data-testid="portal-search-input"
            />
            <select value={searchClass} onChange={e => setSearchClass(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none text-sm text-gray-600"
              data-testid="portal-class-select">
              <option value="">{language === 'hi' ? 'Class चुनें' : 'Select Class'}</option>
              {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={searchSchools} disabled={loading}
              className="px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50 transition whitespace-nowrap"
              data-testid="portal-search-btn">
              {loading ? '...' : language === 'hi' ? 'खोजें' : 'Search'}
            </button>
          </div>
        </div>

        {/* Quick Tags + Nearby */}
        <div className="flex flex-wrap justify-center gap-2 mt-4">
          <button onClick={findNearby} disabled={locatingUser}
            className="px-4 py-1.5 bg-blue-500 text-white rounded-full text-xs font-medium hover:bg-blue-600 transition disabled:opacity-50"
            data-testid="nearby-btn">
            <i className="fa-solid fa-location-crosshairs mr-1"></i>
            {locatingUser ? '...' : (language === 'hi' ? 'Nearby Schools' : 'Nearby Schools')}
          </button>
          {['CBSE', 'State Board', 'English Medium', 'Hindi Medium'].map(tag => (
            <button key={tag} onClick={() => { setSearchQuery(tag); searchSchools(); }}
              className="px-3 py-1 bg-white border rounded-full text-xs text-gray-500 hover:border-orange-300 hover:text-orange-600 transition">
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Nearby Schools Map */}
      {showMap && nearbySchools.length > 0 && step === 'search' && (
        <div className="max-w-6xl mx-auto px-4 mb-6" data-testid="nearby-map-section">
          <div className="bg-white rounded-2xl shadow-sm border p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800 text-sm flex items-center gap-2">
                <i className="fa-solid fa-location-dot text-blue-500"></i>
                {language === 'hi' ? `आपके पास ${nearbySchools.length} schools (10 KM)` : `${nearbySchools.length} schools near you (10 KM)`}
              </h3>
              <button onClick={() => setShowMap(false)} className="text-gray-400 hover:text-gray-600 text-sm">
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {nearbySchools.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition cursor-pointer" data-testid={`nearby-school-${i}`}>
                  <div>
                    <p className="font-medium text-sm text-gray-800">{s.school_name || s.name}</p>
                    <p className="text-xs text-gray-400">{s.district}, {s.state} {s.pincode && `— ${s.pincode}`}</p>
                  </div>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold shrink-0">
                    {s.distance_km} km
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        {schools.length > 0 && (
          <div className="mb-4">
            <p className="text-sm text-gray-400">{schools.length} {language === 'hi' ? 'स्कूल मिले' : 'schools found'}</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {schools.map((school, i) => (
            <div key={school.school_id || school.id || i}
              className="bg-white rounded-2xl shadow-sm border hover:shadow-lg transition-all cursor-pointer group overflow-hidden"
              onClick={() => selectSchool(school)} data-testid={`school-card-${i}`}>
              <div className="h-2 bg-gradient-to-r from-orange-500 to-amber-500"></div>
              <div className="p-5">
                <h3 className="font-bold text-gray-800 text-base mb-1 group-hover:text-orange-600 transition">{school.name}</h3>
                <p className="text-xs text-gray-400 mb-3">{typeof school.address === 'object' ? [school.address?.city, school.address?.state].filter(Boolean).join(', ') : (school.address || school.city || '')}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {school.board && <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs font-medium">{school.board}</span>}
                  {school.medium && <span className="px-2 py-0.5 bg-green-50 text-green-600 rounded text-xs font-medium">{school.medium}</span>}
                  {school.student_count > 0 && <span className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded text-xs font-medium">{school.student_count} students</span>}
                  {school.fees && <span className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded text-xs font-medium">₹{(school.fees.min_annual/1000).toFixed(0)}K{school.fees.max_annual !== school.fees.min_annual ? `-${(school.fees.max_annual/1000).toFixed(0)}K` : ''}/yr</span>}
                </div>
                <div className="flex gap-2">
                  <button className="flex-1 py-2 bg-orange-50 text-orange-600 rounded-lg text-sm font-medium group-hover:bg-orange-500 group-hover:text-white transition">
                    {language === 'hi' ? 'विवरण देखें' : 'View Details'}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); const sid = school.school_id || school.id; setCompareList(prev => prev.find(s => (s.school_id || s.id) === sid) ? prev.filter(s => (s.school_id || s.id) !== sid) : prev.length < 3 ? [...prev, school] : prev); }}
                    className={`px-3 py-2 rounded-lg text-sm border transition ${compareList.find(s => (s.school_id || s.id) === (school.school_id || school.id)) ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-400 border-gray-200 hover:border-blue-300'}`}
                    data-testid={`compare-btn-${i}`}>
                    <i className="fa-solid fa-scale-balanced"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {schools.length === 0 && !loading && (
          <div className="text-center py-16">
            <i className="fa-solid fa-school text-5xl text-gray-200 mb-4 block"></i>
            <p className="text-gray-400 font-medium">{language === 'hi' ? 'Schools खोजने के लिए ऊपर search करें' : 'Search above to find schools'}</p>
          </div>
        )}
      </div>

      {/* Compare Floating Bar */}
      {compareList.length >= 2 && step === 'search' && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-4 z-50" data-testid="compare-bar">
          <span className="text-sm">{compareList.length} {language === 'hi' ? 'schools selected' : 'schools selected'}</span>
          <button onClick={() => setStep('compare')} className="px-4 py-2 bg-blue-500 rounded-xl text-sm font-medium hover:bg-blue-600" data-testid="compare-now-btn">
            <i className="fa-solid fa-scale-balanced mr-1"></i> {language === 'hi' ? 'Compare करें' : 'Compare'}
          </button>
          <button onClick={() => setCompareList([])} className="text-white/50 hover:text-white"><i className="fa-solid fa-times"></i></button>
        </div>
      )}

      {/* Features Strip */}
      <div className="bg-slate-900 py-8 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { icon: 'fa-magnifying-glass', label: language === 'hi' ? 'School खोजें' : 'Find School', color: 'text-blue-400' },
            { icon: 'fa-chair', label: language === 'hi' ? 'Seat Check करें' : 'Check Seats', color: 'text-green-400' },
            { icon: 'fa-gamepad', label: language === 'hi' ? 'Fun Exam दें' : 'Take Fun Exam', color: 'text-purple-400' },
            { icon: 'fa-file-lines', label: language === 'hi' ? 'Online Apply' : 'Apply Online', color: 'text-orange-400' },
          ].map((f, i) => (
            <div key={i} className="text-white">
              <i className={`fa-solid ${f.icon} text-2xl ${f.color} mb-2 block`}></i>
              <p className="text-sm font-medium opacity-80">{f.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // School Detail Screen
  if (step === 'school' && selectedSchool) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
      <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => { setStep('search'); setSelectedSchool(null); }} className="text-gray-400 hover:text-gray-700">
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <span className="font-semibold text-gray-800 text-sm truncate">{selectedSchool.name}</span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="portal-school-detail">
        {/* School Info */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedSchool.name}</h1>
          <p className="text-sm text-gray-400 mb-4">{typeof selectedSchool.address === 'object' ? [selectedSchool.address?.line1, selectedSchool.address?.city, selectedSchool.address?.state].filter(Boolean).join(', ') : (selectedSchool.address || selectedSchool.city || '')}</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedSchool.board && <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium">{selectedSchool.board}</span>}
            {selectedSchool.medium && <span className="px-3 py-1 bg-green-50 text-green-600 rounded-lg text-sm font-medium">{selectedSchool.medium}</span>}
            {selectedSchool.student_count > 0 && <span className="px-3 py-1 bg-purple-50 text-purple-600 rounded-lg text-sm font-medium">{selectedSchool.student_count} students</span>}
          </div>
        </div>

        {/* Seat Availability */}
        {seats && seats.classes && (
          <div className="bg-white rounded-2xl shadow-sm border p-6" data-testid="portal-seats">
            <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="fa-solid fa-chair text-green-500"></i>
              {language === 'hi' ? 'Seat उपलब्धता' : 'Seat Availability'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(seats.classes || []).map((cls, i) => {
                const pct = cls.total > 0 ? Math.round((cls.available / cls.total) * 100) : 0;
                return (
                  <div key={i} className="p-3 rounded-xl border bg-gray-50">
                    <p className="font-semibold text-sm text-gray-700">{cls.class_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div className={`h-2 rounded-full ${pct > 30 ? 'bg-green-500' : pct > 0 ? 'bg-yellow-500' : 'bg-red-400'}`} style={{ width: `${100 - pct}%` }} />
                      </div>
                      <span className={`text-xs font-bold ${cls.available > 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {cls.available > 0 ? `${cls.available} seats` : 'FULL'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Available Exams */}
        {exams.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border p-6" data-testid="portal-exams">
            <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="fa-solid fa-gamepad text-purple-500"></i>
              {language === 'hi' ? 'Entrance Exam' : 'Entrance Exam'}
            </h2>
            <div className="space-y-3">
              {exams.map((ex, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <div>
                    <p className="font-semibold text-sm">{ex.class_name} — {ex.total_questions} rounds</p>
                    <p className="text-xs text-gray-400">{ex.time_limit_minutes} min | {ex.difficulty === 'easy' ? 'आसान' : ex.difficulty === 'hard' ? 'कठिन' : 'मध्यम'}</p>
                  </div>
                  <button onClick={() => navigate(`/admission-exam/${ex.token}`)}
                    className="px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600">
                    {language === 'hi' ? 'Exam दें' : 'Take Exam'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Apply Button */}
        <div className="flex gap-3">
          {exams.length > 0 && (
            <button onClick={() => setStep('seats')}
              className="flex-1 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition">
              <i className="fa-solid fa-chair mr-2"></i>
              {language === 'hi' ? 'Seat बुक करें' : 'Book Seat'}
            </button>
          )}
          <button onClick={() => setStep('apply')}
            className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition"
            data-testid="portal-apply-btn">
            <i className="fa-solid fa-file-lines mr-2"></i>
            {language === 'hi' ? 'Admission के लिए Apply करें' : 'Apply for Admission'}
          </button>
        </div>
      </div>
    </div>
  );

  // Apply Form Screen
  if (step === 'apply') return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
      <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => setStep('school')} className="text-gray-400 hover:text-gray-700">
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <span className="font-semibold text-gray-800 text-sm">{language === 'hi' ? 'Admission Form' : 'Application Form'}</span>
        </div>
      </header>
      <div className="max-w-lg mx-auto px-4 py-8" data-testid="portal-apply-form">
        <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-4">
          <div className="text-center mb-4">
            <p className="text-sm text-orange-500 font-semibold">{selectedSchool?.name}</p>
            <h2 className="text-xl font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {language === 'hi' ? 'Admission Application' : 'Admission Application'}
            </h2>
          </div>
          {[
            { key: 'student_name', label: language === 'hi' ? 'बच्चे का नाम' : 'Student Name', placeholder: 'राहुल शर्मा', type: 'text' },
            { key: 'parent_name', label: language === 'hi' ? 'अभिभावक का नाम' : 'Parent Name', placeholder: 'श्री शर्मा', type: 'text' },
            { key: 'parent_phone', label: language === 'hi' ? 'Phone Number' : 'Phone Number', placeholder: '9876543210', type: 'tel' },
            { key: 'address', label: language === 'hi' ? 'पता' : 'Address', placeholder: 'पता लिखें', type: 'text' },
          ].map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-600 mb-1">{field.label}</label>
              <input type={field.type} value={applyForm[field.key]}
                onChange={e => setApplyForm({ ...applyForm, [field.key]: e.target.value })}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none"
                placeholder={field.placeholder} data-testid={`apply-${field.key}`} />
            </div>
          ))}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">{language === 'hi' ? 'Class' : 'Class Applied'}</label>
            <select value={applyForm.class_name} onChange={e => setApplyForm({ ...applyForm, class_name: e.target.value })}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none"
              data-testid="apply-class">
              <option value="">{language === 'hi' ? 'Class चुनें' : 'Select Class'}</option>
              {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button onClick={submitApplication}
            disabled={loading || !applyForm.student_name || !applyForm.parent_phone || !applyForm.class_name}
            className="w-full py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-40 transition"
            data-testid="apply-submit-btn">
            {loading ? '...' : language === 'hi' ? 'Apply करें' : 'Submit Application'}
          </button>
        </div>
      </div>
    </div>
  );

  // Done Screen
  if (step === 'done') return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center" data-testid="portal-done">
        <div className="text-6xl mb-4">🎉</div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {language === 'hi' ? 'Application Submit हो गया!' : 'Application Submitted!'}
        </h1>
        <p className="text-gray-500 mb-4">
          {language === 'hi' ? `${selectedSchool?.name} को आपकी application मिल गई है। जल्दी ही school आपसे संपर्क करेगा।` : `${selectedSchool?.name} has received your application. The school will contact you soon.`}
        </p>
        <div className="bg-green-50 rounded-xl p-4 border border-green-100 mb-6 text-left">
          <p className="text-sm text-green-700"><strong>{language === 'hi' ? 'बच्चा:' : 'Student:'}</strong> {applyForm.student_name}</p>
          <p className="text-sm text-green-700"><strong>Class:</strong> {applyForm.class_name}</p>
          <p className="text-sm text-green-700"><strong>Phone:</strong> {applyForm.parent_phone}</p>
        </div>
        <button onClick={() => { setStep('search'); setSelectedSchool(null); setSchools([]); setApplied(false); }}
          className="w-full py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600"
          data-testid="portal-back-btn">
          {language === 'hi' ? 'और Schools देखें' : 'Browse More Schools'}
        </button>
      </div>
    </div>
  );

  // Compare Screen
  if (step === 'compare' && compareList.length >= 2) {
    const getAddr = (s) => typeof s.address === 'object' ? [s.address?.city, s.address?.state].filter(Boolean).join(', ') : (s.address || s.city || '');
    const rows = [
      { label: language === 'hi' ? 'Board' : 'Board', key: 'board' },
      { label: language === 'hi' ? 'Medium' : 'Medium', key: 'medium' },
      { label: language === 'hi' ? 'Students' : 'Students', key: 'student_count' },
      { label: language === 'hi' ? 'Fees' : 'Fees', fn: (s) => s.fees ? `₹${(s.fees.min_annual/1000).toFixed(0)}K${s.fees.max_annual !== s.fees.min_annual ? ` - ₹${(s.fees.max_annual/1000).toFixed(0)}K` : ''}/yr` : '—' },
      { label: language === 'hi' ? 'पता' : 'Address', fn: getAddr },
    ];
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
            <button onClick={() => setStep('search')} className="text-gray-400 hover:text-gray-700"><i className="fa-solid fa-arrow-left"></i></button>
            <span className="font-semibold text-gray-800 text-sm"><i className="fa-solid fa-scale-balanced mr-2 text-blue-500"></i>{language === 'hi' ? 'School तुलना' : 'School Comparison'}</span>
          </div>
        </header>
        <div className="max-w-5xl mx-auto px-4 py-6" data-testid="compare-screen">
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="p-4 text-left text-sm font-medium text-gray-500 w-32">{language === 'hi' ? 'पैरामीटर' : 'Parameter'}</th>
                  {compareList.map((s, i) => (
                    <th key={i} className="p-4 text-center border-l">
                      <p className="font-bold text-gray-800 text-sm">{s.name}</p>
                      <p className="text-xs text-gray-400">{getAddr(s)}</p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} className="border-t">
                    <td className="p-4 text-sm text-gray-500 font-medium">{row.label}</td>
                    {compareList.map((s, i) => (
                      <td key={i} className="p-4 text-center border-l text-sm text-gray-700">
                        {row.fn ? row.fn(s) : (s[row.key] || '-')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {compareList.map((s, i) => (
              <button key={i} onClick={() => { selectSchool(s); }} className="py-3 bg-orange-500 text-white rounded-xl font-medium text-sm hover:bg-orange-600" data-testid={`compare-apply-${i}`}>
                {s.name} — {language === 'hi' ? 'Apply करें' : 'Apply'}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
